import React, { useMemo, useState } from "react";

const DNA_STORAGE_KEY = "medgen_dna_research_studio_snapshots_v1";

const BASE_COLORS = {
  A: "#2563eb",
  T: "#f97316",
  G: "#16a34a",
  C: "#9333ea",
  X: "#64748b",
};

const COMPLEMENT = {
  A: "T",
  T: "A",
  G: "C",
  C: "G",
};

const DEFAULT_DNA = [
  { left: "A", right: "T", gene: "GENE-A1", note: "Normal adenine-thymine pair" },
  { left: "G", right: "C", gene: "GENE-B2", note: "Normal guanine-cytosine pair" },
  { left: "T", right: "A", gene: "GENE-C3", note: "Normal thymine-adenine pair" },
  { left: "C", right: "G", gene: "GENE-D4", note: "Normal cytosine-guanine pair" },
  { left: "A", right: "T", gene: "GENE-E5", note: "Metabolic regulation segment" },
  { left: "G", right: "C", gene: "GENE-F6", note: "Protein expression segment" },
  { left: "C", right: "G", gene: "GENE-G7", note: "Immune response segment" },
  { left: "T", right: "A", gene: "GENE-H8", note: "Neurological expression segment" },
  { left: "A", right: "T", gene: "GENE-I9", note: "Cardiac expression segment" },
  { left: "G", right: "C", gene: "GENE-J10", note: "Drug metabolism segment" },
];

const MEDICINE_TEMPLATES = [
  {
    id: "mrna",
    name: "mRNA Candidate",
    type: "genetic_expression",
    purpose: "Temporary instruction layer to express or suppress a protein target.",
  },
  {
    id: "sirna",
    name: "siRNA Candidate",
    type: "gene_silencing",
    purpose: "Designed to reduce expression of a selected gene pathway.",
  },
  {
    id: "crispr-guide",
    name: "CRISPR Guide Candidate",
    type: "gene_editing_research",
    purpose: "Research-only guide candidate for modelling targeted DNA edits.",
  },
  {
    id: "small-molecule",
    name: "Small Molecule Support",
    type: "pathway_modulation",
    purpose: "Chemical pathway candidate to support downstream protein or enzyme modulation.",
  },
];

function uid(prefix = "id") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function readSnapshots() {
  try {
    const raw = window.localStorage.getItem(DNA_STORAGE_KEY);
    const rows = JSON.parse(raw || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

function writeSnapshots(rows) {
  window.localStorage.setItem(DNA_STORAGE_KEY, JSON.stringify(rows));
}

function normalizeBase(value) {
  const base = String(value || "").trim().toUpperCase();
  return ["A", "T", "G", "C"].includes(base) ? base : "X";
}

function getMutationType(pair) {
  if (!pair) return "unknown";
  if (pair.deleted) return "deleted";
  if (pair.inserted) return "inserted";
  if (COMPLEMENT[pair.left] !== pair.right) return "mismatch";
  if (pair.originalLeft && pair.originalLeft !== pair.left) return "substitution";
  if (pair.originalRight && pair.originalRight !== pair.right) return "substitution";
  return "normal";
}

function mutationRisk(pair) {
  const type = getMutationType(pair);
  if (type === "deleted") return 0.8;
  if (type === "mismatch") return 0.65;
  if (type === "substitution") return 0.45;
  if (type === "inserted") return 0.35;
  return 0.1;
}

function riskLabel(score) {
  if (score >= 0.65) return "high";
  if (score >= 0.35) return "moderate";
  return "low";
}

function formatDate(value) {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "No date";
  }
}

export default function DNAResearchStudio({
  patient = null,
  patientResearchContext = null,
  onAddToAvatarQueue,
  onRunAvatarSimulation,
}) {
  const [dnaPairs, setDnaPairs] = useState(() =>
    DEFAULT_DNA.map((pair, index) => ({
      ...pair,
      id: uid("dna"),
      index,
      originalLeft: pair.left,
      originalRight: pair.right,
      deleted: false,
      inserted: false,
    }))
  );

  const [selectedPairId, setSelectedPairId] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("mrna");
  const [medicineName, setMedicineName] = useState("");
  const [medicineNotes, setMedicineNotes] = useState("");
  const [snapshots, setSnapshots] = useState(() => readSnapshots());
  const [status, setStatus] = useState(
    "DNA studio ready. Select a base pair, mutate/remove/insert, then prepare a research-only medicine candidate."
  );

  const selectedPair = useMemo(
    () => dnaPairs.find((pair) => pair.id === selectedPairId) || null,
    [dnaPairs, selectedPairId]
  );

  const selectedTemplate = useMemo(
    () => MEDICINE_TEMPLATES.find((item) => item.id === selectedTemplateId) || MEDICINE_TEMPLATES[0],
    [selectedTemplateId]
  );

  const mutationSummary = useMemo(() => {
    const rows = dnaPairs.map((pair) => ({
      ...pair,
      mutation_type: getMutationType(pair),
      risk_score: mutationRisk(pair),
    }));

    const mutated = rows.filter((row) => row.mutation_type !== "normal");
    const maxRisk = rows.length ? Math.max(...rows.map((row) => row.risk_score)) : 0;

    return {
      total: rows.length,
      mutated: mutated.length,
      deleted: rows.filter((row) => row.mutation_type === "deleted").length,
      mismatch: rows.filter((row) => row.mutation_type === "mismatch").length,
      substitution: rows.filter((row) => row.mutation_type === "substitution").length,
      inserted: rows.filter((row) => row.mutation_type === "inserted").length,
      maxRisk,
      risk: riskLabel(maxRisk),
      rows,
    };
  }, [dnaPairs]);

  const medicinePayload = useMemo(() => {
    const targetPair = selectedPair || mutationSummary.rows.find((row) => row.mutation_type !== "normal") || null;

    return {
      id: uid("dna-medicine"),
      name:
        medicineName.trim() ||
        `${selectedTemplate.name} for ${targetPair?.gene || "DNA target"}`,
      template_id: selectedTemplate.id,
      type: selectedTemplate.type,
      purpose: selectedTemplate.purpose,
      research_only: true,
      patient_id: patient?.id || patientResearchContext?.patient_id || null,
      patient_context: {
        patient_name: patient?.name || patientResearchContext?.patient_name || null,
        age: patient?.age || patientResearchContext?.age || null,
        gender: patient?.gender || patientResearchContext?.gender || null,
        summary:
          patientResearchContext?.summary ||
          patientResearchContext?.initial_summary ||
          patientResearchContext?.doctor_note ||
          null,
      },
      target_gene: targetPair?.gene || null,
      target_pair: targetPair
        ? {
            left: targetPair.left,
            right: targetPair.right,
            original_left: targetPair.originalLeft,
            original_right: targetPair.originalRight,
            mutation_type: getMutationType(targetPair),
            risk_score: mutationRisk(targetPair),
          }
        : null,
      dna_model: mutationSummary.rows,
      mutation_summary: mutationSummary,
      notes: medicineNotes,
      disclaimer:
        "Research-only digital avatar model. Not diagnosis, prescription, clinical treatment, or safety approval.",
      created_at: new Date().toISOString(),
    };
  }, [
    medicineName,
    selectedTemplate,
    selectedPair,
    mutationSummary,
    patient,
    patientResearchContext,
    medicineNotes,
  ]);

  const mutateSelected = (leftBase) => {
    if (!selectedPair) {
      setStatus("Select a DNA pair first.");
      return;
    }

    const left = normalizeBase(leftBase);
    const right = COMPLEMENT[left] || "X";

    setDnaPairs((prev) =>
      prev.map((pair) =>
        pair.id === selectedPair.id
          ? {
              ...pair,
              left,
              right,
              deleted: false,
              note: `Mutated to ${left}-${right}`,
            }
          : pair
      )
    );

    setStatus(`Pair mutated to ${left}-${right}.`);
  };

  const forceMismatch = () => {
    if (!selectedPair) {
      setStatus("Select a DNA pair first.");
      return;
    }

    const options = ["A", "T", "G", "C"].filter(
      (base) => base !== COMPLEMENT[selectedPair.left]
    );

    const wrongRight = options[0] || "X";

    setDnaPairs((prev) =>
      prev.map((pair) =>
        pair.id === selectedPair.id
          ? {
              ...pair,
              right: wrongRight,
              deleted: false,
              note: `Mismatch introduced: ${pair.left}-${wrongRight}`,
            }
          : pair
      )
    );

    setStatus("Mismatch introduced. This will show higher risk in avatar payload.");
  };

  const deleteSelected = () => {
    if (!selectedPair) {
      setStatus("Select a DNA pair first.");
      return;
    }

    setDnaPairs((prev) =>
      prev.map((pair) =>
        pair.id === selectedPair.id
          ? {
              ...pair,
              deleted: true,
              left: "X",
              right: "X",
              note: "Base pair removed from model",
            }
          : pair
      )
    );

    setStatus("Base pair removed from DNA model.");
  };

  const restoreSelected = () => {
    if (!selectedPair) {
      setStatus("Select a DNA pair first.");
      return;
    }

    setDnaPairs((prev) =>
      prev.map((pair) =>
        pair.id === selectedPair.id
          ? {
              ...pair,
              left: pair.originalLeft || "A",
              right: pair.originalRight || "T",
              deleted: false,
              inserted: false,
              note: "Restored to original pair",
            }
          : pair
      )
    );

    setStatus("Selected pair restored.");
  };

  const insertAfterSelected = () => {
    const index = selectedPair
      ? dnaPairs.findIndex((pair) => pair.id === selectedPair.id)
      : dnaPairs.length - 1;

    const newPair = {
      id: uid("dna"),
      index: index + 1,
      left: "A",
      right: "T",
      originalLeft: "A",
      originalRight: "T",
      gene: `INSERT-${dnaPairs.length + 1}`,
      note: "Inserted research pair",
      inserted: true,
      deleted: false,
    };

    const next = [...dnaPairs];
    next.splice(index + 1, 0, newPair);

    setDnaPairs(next.map((pair, idx) => ({ ...pair, index: idx })));
    setSelectedPairId(newPair.id);
    setStatus("New A-T base pair inserted.");
  };

  const resetDna = () => {
    setDnaPairs(
      DEFAULT_DNA.map((pair, index) => ({
        ...pair,
        id: uid("dna"),
        index,
        originalLeft: pair.left,
        originalRight: pair.right,
        deleted: false,
        inserted: false,
      }))
    );
    setSelectedPairId(null);
    setStatus("DNA model reset to normal human template.");
  };

  const saveSnapshot = () => {
    const snapshot = {
      id: uid("dna-snapshot"),
      title: `${medicinePayload.name} · ${mutationSummary.risk}`,
      created_at: new Date().toISOString(),
      dnaPairs,
      medicinePayload,
      mutationSummary,
    };

    const next = [snapshot, ...snapshots].slice(0, 30);
    setSnapshots(next);
    writeSnapshots(next);
    setStatus("DNA + medicine preparation snapshot saved locally.");
  };

  const loadSnapshot = (snapshot) => {
    setDnaPairs(snapshot.dnaPairs || []);
    setSelectedPairId(null);
    setMedicineName(snapshot.medicinePayload?.name || "");
    setMedicineNotes(snapshot.medicinePayload?.notes || "");
    setSelectedTemplateId(snapshot.medicinePayload?.template_id || "mrna");
    setStatus(`Loaded snapshot: ${snapshot.title}`);
  };

  const deleteSnapshot = (id) => {
    const next = snapshots.filter((item) => item.id !== id);
    setSnapshots(next);
    writeSnapshots(next);
    setStatus("Snapshot deleted.");
  };

  const addMedicineToAvatarQueue = () => {
    const queueItem = {
      compound: {
        id: medicinePayload.id,
        name: medicinePayload.name,
        formula: `${selectedTemplate.type}:${medicinePayload.target_gene || "DNA"}`,
        display_formula: `${selectedTemplate.name} → ${medicinePayload.target_gene || "DNA target"}`,
        compound_type: "dna_prepared_medicine_candidate",
        description: medicinePayload.purpose,
        is_designed: true,
      },
      atoms: [],
      bonds: [],
      graph_nodes: [],
      graph_edges: [],
      dna_model: dnaPairs,
      medicine_payload: medicinePayload,
      mutation_summary: mutationSummary,
      source: "dna_research_studio",
    };

    onAddToAvatarQueue?.(queueItem);
    saveSnapshot();
    setStatus("DNA medicine candidate added to avatar queue and snapshot saved.");
  };

  return (
    <section className="dna-studio">
      <style>{dnaCss}</style>

      <div className="dna-hero">
        <div>
          <p>DNA RESEARCH MODE</p>
          <h2>DNA modelling and medicine preparation</h2>
          <span>
            Build a normal DNA strand, mutate or remove A-T / G-C pairs, then prepare a research-only medicine candidate for digital avatar simulation.
          </span>
        </div>

        <div className={`dna-risk-card ${mutationSummary.risk}`}>
          <strong>{mutationSummary.risk}</strong>
          <span>model risk</span>
          <small>{mutationSummary.mutated} edited pairs / {mutationSummary.total}</small>
        </div>
      </div>

      <div className="dna-layout">
        <aside className="dna-side">
          <div className="dna-card">
            <div className="dna-card-title">Selected base pair</div>

            {selectedPair ? (
              <>
                <h3>
                  {selectedPair.left}-{selectedPair.right}
                </h3>
                <p>{selectedPair.gene}</p>
                <span className={`dna-chip ${getMutationType(selectedPair)}`}>
                  {getMutationType(selectedPair)}
                </span>

                <div className="dna-selected-meta">
                  <strong>Original</strong>
                  <span>
                    {selectedPair.originalLeft}-{selectedPair.originalRight}
                  </span>
                  <strong>Current</strong>
                  <span>
                    {selectedPair.left}-{selectedPair.right}
                  </span>
                  <strong>Risk score</strong>
                  <span>{mutationRisk(selectedPair).toFixed(2)}</span>
                </div>
              </>
            ) : (
              <p>Select a base pair from the helix.</p>
            )}
          </div>

          <div className="dna-card">
            <div className="dna-card-title">DNA edit actions</div>

            <div className="dna-button-grid">
              {["A", "T", "G", "C"].map((base) => (
                <button key={base} type="button" onClick={() => mutateSelected(base)}>
                  Mutate to {base}-{COMPLEMENT[base]}
                </button>
              ))}
            </div>

            <button type="button" onClick={forceMismatch}>
              Force mismatch
            </button>
            <button type="button" onClick={deleteSelected} className="danger">
              Remove selected pair
            </button>
            <button type="button" onClick={insertAfterSelected}>
              Insert new A-T pair
            </button>
            <button type="button" onClick={restoreSelected}>
              Restore selected
            </button>
            <button type="button" onClick={resetDna} className="dark">
              Reset normal DNA
            </button>
          </div>

          <div className="dna-card">
            <div className="dna-card-title">Mutation summary</div>
            <div className="dna-summary-grid">
              <div>
                <strong>{mutationSummary.total}</strong>
                <span>Total pairs</span>
              </div>
              <div>
                <strong>{mutationSummary.mutated}</strong>
                <span>Edited</span>
              </div>
              <div>
                <strong>{mutationSummary.deleted}</strong>
                <span>Deleted</span>
              </div>
              <div>
                <strong>{mutationSummary.mismatch}</strong>
                <span>Mismatch</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="dna-main">
          <div className="dna-toolbar">
            <div>
              <strong>DNA double helix workspace</strong>
              <span>{status}</span>
            </div>

            <div className="dna-toolbar-actions">
              <button type="button" onClick={saveSnapshot}>
                Save snapshot
              </button>
              <button type="button" onClick={addMedicineToAvatarQueue}>
                Add medicine to avatar queue
              </button>
              <button type="button" className="primary" onClick={onRunAvatarSimulation}>
                Run avatar simulation
              </button>
            </div>
          </div>

          <div className="dna-helix">
            <svg viewBox="0 0 920 780" className="dna-svg">
              <defs>
                <linearGradient id="dnaBackboneA" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#18b7ff" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#1f7bc1" stopOpacity="0.4" />
                </linearGradient>

                <linearGradient id="dnaBackboneB" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#9333ea" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#be185d" stopOpacity="0.4" />
                </linearGradient>

                <filter id="dnaGlow">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <rect width="920" height="780" rx="28" fill="#ffffff" />

              <path
                d={makeBackbonePath(dnaPairs, "left")}
                fill="none"
                stroke="url(#dnaBackboneA)"
                strokeWidth="8"
                strokeLinecap="round"
                filter="url(#dnaGlow)"
              />

              <path
                d={makeBackbonePath(dnaPairs, "right")}
                fill="none"
                stroke="url(#dnaBackboneB)"
                strokeWidth="8"
                strokeLinecap="round"
                filter="url(#dnaGlow)"
              />

              {dnaPairs.map((pair, index) => {
                const y = 70 + index * 68;
                const wave = Math.sin(index * 0.75);
                const leftX = 280 + wave * 90;
                const rightX = 640 - wave * 90;
                const selected = selectedPairId === pair.id;
                const type = getMutationType(pair);
                const risk = mutationRisk(pair);

                return (
                  <g
                    key={pair.id}
                    className="dna-pair-node"
                    onClick={() => {
                      setSelectedPairId(pair.id);
                      setStatus(`${pair.gene} selected.`);
                    }}
                  >
                    <line
                      x1={leftX}
                      y1={y}
                      x2={rightX}
                      y2={y}
                      stroke={type === "normal" ? "#cbd5e1" : risk >= 0.65 ? "#ef4444" : "#f59e0b"}
                      strokeWidth={selected ? "7" : "4"}
                      strokeDasharray={type === "deleted" ? "8 8" : ""}
                      opacity={type === "deleted" ? "0.45" : "1"}
                    />

                    <circle
                      cx={leftX}
                      cy={y}
                      r={selected ? 27 : 23}
                      fill={BASE_COLORS[pair.left] || BASE_COLORS.X}
                      stroke={selected ? "#f59e0b" : "#ffffff"}
                      strokeWidth="4"
                      filter="url(#dnaGlow)"
                    />
                    <circle
                      cx={rightX}
                      cy={y}
                      r={selected ? 27 : 23}
                      fill={BASE_COLORS[pair.right] || BASE_COLORS.X}
                      stroke={selected ? "#f59e0b" : "#ffffff"}
                      strokeWidth="4"
                      filter="url(#dnaGlow)"
                    />

                    <text x={leftX} y={y + 5} textAnchor="middle" fill="white" fontSize="17" fontWeight="950">
                      {pair.left}
                    </text>
                    <text x={rightX} y={y + 5} textAnchor="middle" fill="white" fontSize="17" fontWeight="950">
                      {pair.right}
                    </text>

                    <text x="455" y={y - 12} textAnchor="middle" fill="#07111f" fontSize="12" fontWeight="950">
                      {pair.gene}
                    </text>
                    <text x="455" y={y + 22} textAnchor="middle" fill="#64748b" fontSize="10" fontWeight="850">
                      {type}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </main>

        <aside className="dna-side">
          <div className="dna-card medicine-card">
            <div className="dna-card-title">Medicine preparation</div>

            <label>
              Candidate name
              <input
                value={medicineName}
                onChange={(event) => setMedicineName(event.target.value)}
                placeholder="Auto generated if blank"
              />
            </label>

            <label>
              Medicine strategy
              <select
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
              >
                {MEDICINE_TEMPLATES.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="medicine-template-box">
              <strong>{selectedTemplate.name}</strong>
              <span>{selectedTemplate.type}</span>
              <p>{selectedTemplate.purpose}</p>
            </div>

            <label>
              Research notes
              <textarea
                value={medicineNotes}
                onChange={(event) => setMedicineNotes(event.target.value)}
                placeholder="Expected pathway, gene target, protein expression, organ impact..."
              />
            </label>

            <button type="button" className="primary" onClick={addMedicineToAvatarQueue}>
              Prepare + queue medicine
            </button>
          </div>

          <div className="dna-card payload-card">
            <div className="dna-card-title">Avatar payload preview</div>
            <pre>{JSON.stringify(medicinePayload, null, 2)}</pre>
          </div>

          <div className="dna-card">
            <div className="dna-card-title">Saved snapshots</div>

            <div className="snapshot-list">
              {snapshots.length === 0 ? (
                <p>No DNA snapshots saved yet.</p>
              ) : (
                snapshots.map((snapshot) => (
                  <div key={snapshot.id} className="snapshot-item">
                    <strong>{snapshot.title}</strong>
                    <span>{formatDate(snapshot.created_at)}</span>

                    <div className="snapshot-actions">
                      <button type="button" onClick={() => loadSnapshot(snapshot)}>
                        Load
                      </button>
                      <button type="button" className="danger" onClick={() => deleteSnapshot(snapshot.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function makeBackbonePath(pairs, side) {
  if (!pairs.length) return "";

  const points = pairs.map((_, index) => {
    const y = 70 + index * 68;
    const wave = Math.sin(index * 0.75);
    const x = side === "left" ? 280 + wave * 90 : 640 - wave * 90;
    return `${index === 0 ? "M" : "L"} ${x} ${y}`;
  });

  return points.join(" ");
}

const dnaCss = `
.dna-studio {
  --dna-bg: #f6faff;
  --dna-card: rgba(255,255,255,0.96);
  --dna-border: #cddcec;
  --dna-text: #07111f;
  --dna-muted: #64748b;
  --dna-blue: #1f7bc1;
  --dna-cyan: #18b7ff;
  width: 100%;
  color: var(--dna-text);
}

.dna-studio * {
  box-sizing: border-box;
}

.dna-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px;
  gap: 18px;
  align-items: stretch;
  padding: 24px;
  border-radius: 24px;
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.12), transparent 36%),
    rgba(255,255,255,0.97);
  border: 1px solid var(--dna-border);
  box-shadow: 0 16px 44px rgba(7,17,31,0.06);
}

.dna-hero p,
.dna-card-title {
  margin: 0 0 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.dna-hero h2 {
  margin: 0;
  font-size: clamp(28px, 3.3vw, 44px);
  line-height: 1.05;
  letter-spacing: -0.055em;
  font-weight: 950;
}

.dna-hero span {
  display: block;
  margin-top: 10px;
  color: var(--dna-muted);
  line-height: 1.6;
  font-size: 13px;
}

.dna-risk-card {
  padding: 18px;
  border-radius: 20px;
  color: white;
  display: grid;
  align-content: center;
  background: linear-gradient(145deg, #03060c, #081525);
}

.dna-risk-card.low {
  background: linear-gradient(145deg, #064e3b, #022c22);
}

.dna-risk-card.moderate {
  background: linear-gradient(145deg, #92400e, #451a03);
}

.dna-risk-card.high {
  background: linear-gradient(145deg, #991b1b, #450a0a);
}

.dna-risk-card strong {
  font-size: 32px;
  text-transform: uppercase;
  line-height: 1;
}

.dna-risk-card span,
.dna-risk-card small {
  margin-top: 7px;
  color: rgba(255,255,255,0.78);
  font-size: 11px;
  font-weight: 850;
}

.dna-layout {
  display: grid;
  grid-template-columns: 245px minmax(0, 1fr) 310px;
  gap: 14px;
  align-items: start;
  margin-top: 14px;
}

.dna-side {
  display: grid;
  gap: 12px;
}

.dna-card,
.dna-main {
  padding: 16px;
  border-radius: 20px;
  background: var(--dna-card);
  border: 1px solid var(--dna-border);
  box-shadow: 0 12px 34px rgba(7,17,31,0.055);
}

.dna-card h3 {
  margin: 0;
  font-size: 25px;
  line-height: 1;
  letter-spacing: -0.04em;
  font-weight: 950;
}

.dna-card p {
  margin: 9px 0 0;
  color: var(--dna-muted);
  font-size: 12px;
  line-height: 1.5;
}

.dna-chip {
  display: inline-flex;
  width: fit-content;
  margin-top: 10px;
  padding: 5px 9px;
  border-radius: 999px;
  background: #eef6ff;
  color: #0b4e84;
  border: 1px solid rgba(31,123,193,0.16);
  font-size: 10px;
  font-weight: 950;
}

.dna-chip.deleted,
.dna-chip.mismatch {
  color: #991b1b;
  background: #fff1f2;
  border-color: rgba(239,68,68,0.2);
}

.dna-chip.substitution,
.dna-chip.inserted {
  color: #92400e;
  background: #fffbeb;
  border-color: rgba(245,158,11,0.2);
}

.dna-selected-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #d9e8f6;
}

.dna-selected-meta strong {
  color: #125a94;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.dna-selected-meta span {
  color: var(--dna-text);
  font-size: 12px;
  font-weight: 900;
}

.dna-button-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
  margin-bottom: 8px;
}

.dna-card button,
.dna-toolbar button {
  min-height: 36px;
  padding: 8px 10px;
  border-radius: 11px;
  border: 1px solid var(--dna-border);
  background: #f8fbff;
  color: #102033;
  cursor: pointer;
  font-size: 11px;
  font-weight: 950;
  transition: 0.16s ease;
}

.dna-card button {
  width: 100%;
  margin-top: 7px;
}

.dna-card button:hover,
.dna-toolbar button:hover {
  transform: translateY(-1px);
  border-color: rgba(31,123,193,0.45);
}

.dna-card button.primary,
.dna-toolbar button.primary {
  color: white;
  border-color: transparent;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 10px 22px rgba(31,123,193,0.16);
}

.dna-card button.dark {
  color: white;
  border-color: transparent;
  background: linear-gradient(135deg, #03060c, #081525);
}

.dna-card button.danger,
.snapshot-actions button.danger {
  color: #991b1b;
  background: rgba(239,68,68,0.08);
  border-color: rgba(239,68,68,0.18);
}

.dna-summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.dna-summary-grid div {
  padding: 10px;
  border-radius: 14px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
}

.dna-summary-grid strong {
  display: block;
  font-size: 22px;
  line-height: 1;
  font-weight: 950;
}

.dna-summary-grid span {
  display: block;
  margin-top: 6px;
  color: var(--dna-muted);
  font-size: 10px;
  font-weight: 850;
}

.dna-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 16px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
  margin-bottom: 12px;
}

.dna-toolbar strong {
  display: block;
  font-size: 13px;
  font-weight: 950;
}

.dna-toolbar span {
  display: block;
  margin-top: 4px;
  color: var(--dna-muted);
  font-size: 11px;
  font-weight: 850;
}

.dna-toolbar-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.dna-helix {
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid #d9e8f6;
  background: white;
}

.dna-svg {
  width: 100%;
  min-height: 680px;
  display: block;
}

.dna-pair-node {
  cursor: pointer;
}

.dna-pair-node:hover circle {
  stroke: #f59e0b;
  stroke-width: 5;
}

.medicine-card label {
  display: grid;
  gap: 6px;
  margin-top: 10px;
  color: #44576e;
  font-size: 10px;
  font-weight: 950;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.medicine-card input,
.medicine-card select,
.medicine-card textarea {
  width: 100%;
  min-height: 38px;
  padding: 9px 10px;
  border-radius: 11px;
  border: 1px solid var(--dna-border);
  outline: none;
  background: white;
  color: var(--dna-text);
  font-size: 12px;
  font-weight: 800;
}

.medicine-card textarea {
  min-height: 86px;
  resize: vertical;
}

.medicine-template-box {
  margin-top: 12px;
  padding: 12px;
  border-radius: 15px;
  color: white;
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.20), transparent 34%),
    linear-gradient(145deg, #03060c, #081525);
}

.medicine-template-box strong {
  display: block;
  font-size: 15px;
  font-weight: 950;
}

.medicine-template-box span {
  display: block;
  margin-top: 5px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.medicine-template-box p {
  color: #a9bdd3;
}

.payload-card pre {
  min-height: 220px;
  max-height: 330px;
  overflow: auto;
  padding: 12px;
  margin: 0;
  border-radius: 14px;
  color: #dbeafe;
  background: #07111f;
  border: 1px solid rgba(24,183,255,0.16);
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 10px;
  line-height: 1.5;
}

.snapshot-list {
  display: grid;
  gap: 8px;
  max-height: 260px;
  overflow: auto;
}

.snapshot-item {
  padding: 10px;
  border-radius: 14px;
  border: 1px solid #d9e8f6;
  background: #f7fbff;
}

.snapshot-item strong {
  display: block;
  color: var(--dna-text);
  font-size: 12px;
  font-weight: 950;
}

.snapshot-item span {
  display: block;
  margin-top: 4px;
  color: var(--dna-muted);
  font-size: 10px;
  line-height: 1.35;
}

.snapshot-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
  margin-top: 8px;
}

@media (max-width: 1260px) {
  .dna-layout {
    grid-template-columns: 1fr;
  }

  .dna-side {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .dna-hero,
  .dna-side {
    grid-template-columns: 1fr;
  }

  .dna-toolbar {
    flex-direction: column;
  }

  .dna-toolbar-actions {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
  }

  .dna-toolbar-actions button {
    width: 100%;
  }

  .dna-svg {
    min-height: 560px;
  }
}
`;